// ── 출퇴근 관리 유틸리티 ──────────────────────────────────────────────────────
// 순수 헬퍼 함수 및 공용 상수 (UI·API 의존 없음)
import { toDateOnlyUtc, parseTimestamp } from '../../../utils/date';

/** 페이지 당 표시 행 수 */
export const PAGE_SIZE = 10;
/** 모바일 뷰 전환 기준 너비(px) */
export const MOBILE_BREAKPOINT = 900;

/** 한국 날짜 포맷터 (YYYY-MM-DD) */
export const KST_DATE_FORMATTER = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' });
/** 한국 시간 포맷터 (HH:mm, 24시간) */
export const KST_TIME_FORMATTER = new Intl.DateTimeFormat('ko-KR', {
  timeZone: 'Asia/Seoul',
  hour12: false,
  hour: '2-digit',
  minute: '2-digit',
});

/** 요일 순서 (월~일) */
export const WEEKDAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
/** 요일 한글 레이블 */
export const WEEKDAY_LABELS = { mon: '월', tue: '화', wed: '수', thu: '목', fri: '금', sat: '토', sun: '일' };

// ── IP 정규화 및 표시 ────────────────────────────────────────────────────────

/**
 * IPv4-mapped IPv6 주소(::ffff:x.x.x.x)를 순수 IPv4로 변환
 * @param {string} ip
 * @returns {string} 정규화된 IP
 */
export function normalizeIp(ip) {
  if (!ip) return '-';
  return String(ip).replace(/^::ffff:/, '').trim();
}

/**
 * IP + 별칭 합성 표시 문자열 (예: "192.168.0.1 (본사)")
 * @param {string} ip
 * @param {string} alias
 */
export function formatIpWithAlias(ip, alias) {
  const pureIp = normalizeIp(ip);
  const label = String(alias || '').trim();
  if (!pureIp || pureIp === '-') return '-';
  if (!label) return pureIp;
  return `${pureIp} (${label})`;
}

// ── 날짜/시간 파싱 및 포맷 ──────────────────────────────────────────────────

/**
 * 다양한 포맷의 날짜 값을 Date 객체로 안전 변환
 * 'YYYY-MM-DD' 문자열은 UTC 자정으로 변환 (Intl.DateTimeFormat의 timeZone: Asia/Seoul이 보정)
 * toDateOnlyUtc: utils/date.js 사용 (new Date 직접 사용 금지)
 */
export function toDateSafe(value) {
  if (!value) return null;
  const text = String(value).trim();
  // 'YYYY-MM-DD' → toDateOnlyUtc로 UTC 자정 Date 생성 (new Date 직접 사용 금지)
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return toDateOnlyUtc(text);
  // parseTimestamp: utils/date.js 사용 (new Date 직접 사용 금지, 시간 정보 보존)
  return parseTimestamp(value);
}

/** 날짜 → KST YYYY-MM-DD 포맷 문자열 */
export function fmtDate(value) {
  const dt = toDateSafe(value);
  if (!dt) return '-';
  return KST_DATE_FORMATTER.format(dt);
}

/** 날짜/시간 → KST HH:mm 포맷 문자열 */
export function fmtTime(value) {
  const dt = toDateSafe(value);
  if (!dt) return '-';
  return KST_TIME_FORMATTER.format(dt);
}

/**
 * 날짜 + 시간 문자열을 로컬 타임스탬프 문자열로 합성
 * @returns {'YYYY-MM-DD HH:mm:00' | null}
 */
export function toLocalTimestampString(dateText, timeText) {
  if (!dateText || !timeText) return null;
  return `${dateText} ${timeText}:00`;
}

// ── 시간(HH:mm) 유효성 및 계산 ─────────────────────────────────────────────

/** HH:mm 포맷 유효성 검사 */
export function isValidHHmm(value) {
  return /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(String(value || '').trim());
}

/** HH:mm → 자정 기준 분 (0~1439). 유효하지 않으면 null */
export function hhmmToMinutes(value) {
  if (!isValidHHmm(value)) return null;
  const [h, m] = String(value).split(':').map(Number);
  return h * 60 + m;
}

/**
 * 근로기준법에 따른 자동 휴게시간 계산
 * 4시간 이상: 30분 / 6시간30분 이상: 120분
 */
export function getAutoBreakMinutes(totalMinutes) {
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return 0;
  if (totalMinutes >= 390) return 120; // 6시간 30분 이상
  if (totalMinutes >= 240) return 30;  // 4시간 이상
  return 0;
}

/** 출·퇴근 HH:mm → 총 근무시간(분). 퇴근이 출근보다 이르거나 유효하지 않으면 null */
export function getTotalMinutesByHHmm(checkInHHmm, checkOutHHmm) {
  const start = hhmmToMinutes(checkInHHmm);
  const end = hhmmToMinutes(checkOutHHmm);
  if (start === null || end === null) return null;
  if (end <= start) return null;
  return end - start;
}

/** 행 객체에서 총 근무시간(분) 계산 */
export function getRowTotalMinutes(row) {
  const serverWorked = Number(row?.worked_minutes);
  if (Number.isFinite(serverWorked)) {
    const serverBreak = Number(row?.effective_break_minutes);
    return serverWorked + (Number.isFinite(serverBreak) ? serverBreak : 0);
  }
  const recognized = Number(row?.recognized_total_minutes);
  if (Number.isFinite(recognized)) return recognized;
  return getTotalMinutesByHHmm(row.check_in_hhmm, row.check_out_hhmm);
}

/** 행 객체에서 자동 휴게시간(분) 계산 */
export function getRowAutoBreakMinutes(row, totalMinutes) {
  const fromServer = Number(row?.auto_break_minutes);
  if (Number.isFinite(fromServer)) return fromServer;
  return totalMinutes === null ? null : getAutoBreakMinutes(totalMinutes);
}

/** 행 객체에서 실질 휴게시간(분) 계산 (서버 값 > 직접 입력 > 자동) */
export function getRowBreakMinutes(row) {
  // null/undefined는 fallback 대상이므로 Number()로 변환하지 않음 (Number(null)=0 → 잘못된 0 반환 방지)
  if (row?.effective_break_minutes != null) {
    const effectiveFromServer = Number(row.effective_break_minutes);
    if (Number.isFinite(effectiveFromServer)) return effectiveFromServer;
  }
  const total = getRowTotalMinutes(row);
  if (row.break_minutes === null || row.break_minutes === undefined || row.break_minutes === '') {
    return getRowAutoBreakMinutes(row, total);
  }
  return Number(row.break_minutes);
}

/** 분 → 한국어 표기 (예: "8시간 00분"). 유효하지 않으면 '-' */
export function formatMinutesKorean(totalMinutes) {
  if (!Number.isFinite(totalMinutes) || totalMinutes < 0) return '-';
  const safe = Math.floor(totalMinutes);
  const hour = Math.floor(safe / 60);
  const minute = safe % 60;
  return `${hour}시간 ${String(minute).padStart(2, '0')}분`;
}

// ── 근무 시간 설정 (템플릿) 관련 ───────────────────────────────────────────

/** 유니크한 행 키 생성 (타임스탬프 + 랜덤) */
export function makeTimeSettingRowKey() {
  return `time-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** 기본 주간 근무 규칙 생성 (월~금 근무, 토·일 휴무) */
export function createDefaultWeeklyRules(startTime = '09:30', endTime = '18:00') {
  const workday = { is_workday: true, start_time: startTime, end_time: endTime, break_minutes: null, half_leave_threshold: null, work_location: null };
  const offday  = { is_workday: false, start_time: '', end_time: '', break_minutes: null, half_leave_threshold: null, work_location: null };
  return { mon: { ...workday }, tue: { ...workday }, wed: { ...workday }, thu: { ...workday }, fri: { ...workday }, sat: { ...offday }, sun: { ...offday } };
}

/** 서버 응답 주간 규칙 → 드래프트 형식으로 정규화 */
export function normalizeDraftWeeklyRules(rawRules) {
  const normalized = {};
  WEEKDAY_ORDER.forEach((dayKey) => {
    const raw = rawRules?.[dayKey] || {};
    const rawBreak = raw.break_minutes;
    const breakMinutes = (rawBreak !== null && rawBreak !== undefined && rawBreak !== ''
      && Number.isFinite(Number(rawBreak)))
      ? Math.max(0, Math.floor(Number(rawBreak)))
      : null;
    const rawThresh = raw.half_leave_threshold;
    const halfLeaveThreshold = (rawThresh && isValidHHmm(String(rawThresh).trim())) ? String(rawThresh).trim() : null;
    normalized[dayKey] = {
      is_workday: Boolean(raw.is_workday),
      start_time: String(raw.start_time || ''),
      end_time: String(raw.end_time || ''),
      break_minutes: breakMinutes,
      half_leave_threshold: halfLeaveThreshold,
      work_location: String(raw.work_location || '').trim() || null,
    };
  });
  return normalized;
}

/** 서버 응답 templates 배열 → 드래프트 행 배열로 변환 */
export function toTimeSettingDraftRows(items) {
  const templates = Array.isArray(items?.templates) ? items.templates : (Array.isArray(items) ? items : []);
  return templates.map((item, index) => ({
    _rowKey: makeTimeSettingRowKey(),
    id: String(item?.id || `template_${index + 1}`),
    name: String(item?.name || ''),
    weekly_rules: normalizeDraftWeeklyRules(item?.weekly_rules || createDefaultWeeklyRules()),
    // 계약상 1일 소정근로시간(선택). null/미설정이면 빈 값으로 두어 "자동" 표시.
    prescribed_daily_hours: item?.prescribed_daily_hours ?? null,
  }));
}

/** HH:mm 입력값 자동 정규화 (숫자만 → xx:xx) */
export function normalizeHHmmInput(value) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

/** 지각/조퇴 기준 분 입력값 정규화 (0~180 클램핑) */
export function normalizePolicyMinutesInput(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  const safe = Math.floor(parsed);
  if (safe < 0) return 0;
  if (safe > 180) return 180;
  return safe;
}

/**
 * 드래프트 주간 소정근로시간(분) 계산
 * 서버 calcWeeklyScheduledMinutes 와 동일 로직 (연차 발생 미리보기용)
 */
export function calcDraftWeeklyScheduledMinutes(weeklyRules) {
  let total = 0;
  WEEKDAY_ORDER.forEach((dayKey) => {
    const rule = weeklyRules?.[dayKey];
    if (!rule?.is_workday || !isValidHHmm(rule.start_time) || !isValidHHmm(rule.end_time)) return;
    const start = hhmmToMinutes(rule.start_time);
    const end = hhmmToMinutes(rule.end_time);
    const gross = end - start;
    if (gross <= 0) return;
    let breakMins;
    if (rule.break_minutes !== null && rule.break_minutes !== undefined && Number.isFinite(Number(rule.break_minutes))) {
      breakMins = Math.max(0, Number(rule.break_minutes));
    } else {
      breakMins = gross >= 480 ? 60 : gross >= 240 ? 30 : 0;
    }
    total += Math.max(0, gross - breakMins);
  });
  return total;
}

/** 소정 휴게시간(분) 입력값 정규화 (빈 문자열 → null, 숫자 → 0~720 클램핑) */
export function normalizeBreakMinutesInput(value) {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const safe = Math.floor(parsed);
  if (safe < 0) return 0;
  if (safe > 720) return 720;
  return safe;
}

// ── 출퇴근 빠른 필터 정의 + 행 매치 (Phase 1-2 A4 / B) ───────────────────────
// 색상은 모바일 바 시각화에도 그대로 사용됨 (B): normal=teal, late=amber, early=orange, missing_out=red, dayoff=slate-gray
export const ATTENDANCE_FILTER_DEFS = [
  { key: 'normal',      label: '정상',     color: '#0f766e' },
  { key: 'late',        label: '지각',     color: '#d97706' },
  { key: 'early',       label: '조퇴',     color: '#c2410c' },
  { key: 'missing_out', label: '퇴근미체크', color: '#dc2626' },
  { key: 'dayoff',      label: '휴무',     color: '#64748b' },
];

// 행이 주어진 키에 해당하는지 판정.
// 'normal'은 휴무일을 제외 (휴무일은 dayoff에서만 카운트되도록).
// ── 예외 스케줄 (attendance_schedule_overrides) 표시 helper ───────────────────
// 2026-04-28: 출퇴근 달력에서 등록된 예외를 출퇴근 관리 화면에 동일 톤으로 표시.
// 반환 값:
//   kind: 'absence' (휴무, is_workday=false) | 'modified' (시간 변경, is_workday=true) | null (예외 없음)
//   label: 셀에 표시할 텍스트 (휴무 → reason or '휴무' / modified → 'HH:MM~HH:MM')
//   color: { bg, text } Tailwind 클래스 — absence=보라, modified=파랑
//   reasonText: 비고/툴팁용 reason (자유 텍스트)
export function getOverrideStatus(row) {
  if (!row || row.override_is_workday === null || row.override_is_workday === undefined) return null;
  const reason = (row.override_reason || '').trim() || null;
  if (row.override_is_workday === false) {
    return {
      kind: 'absence',
      label: reason || '휴무',
      color: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300' },
      reasonText: reason,
      modifiedTime: null,
    };
  }
  // is_workday=true → 시간 변경
  const start = row.override_start_time || '';
  const end = row.override_end_time || '';
  const time = (start && end) ? `${start}~${end}` : null;
  return {
    kind: 'modified',
    label: reason || time || '조정근무',
    color: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300' },
    reasonText: reason,
    modifiedTime: time,
  };
}

export function matchesAttendanceFilter(row, key) {
  const lateMin = Number(row?.late_minutes || 0);
  const earlyMin = Number(row?.early_leave_minutes || 0);
  const hasIn = Boolean(row?.check_in);
  const hasOut = Boolean(row?.check_out);
  const isDayoff = Boolean(row?.is_dayoff);
  if (key === 'dayoff')      return isDayoff;
  if (key === 'late')        return lateMin > 0;
  if (key === 'early')       return earlyMin > 0;
  if (key === 'missing_out') return hasIn && !hasOut;
  if (key === 'normal')      return !isDayoff && hasIn && lateMin === 0 && earlyMin === 0 && hasOut;
  return true;
}
